import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Trash2, Loader2, AlertCircle, Plus, Check } from "lucide-react";

// Fix WH42 (2026-09-09): pedido de Gabriela — registrar la merma REAL (lo que se
// tira) para que quede medida y valorizada. No confundir con el `rendimiento`
// del catálogo, que es la merma teórica de limpieza y ya está en el cálculo de
// compra: aquélla se descuenta sola, ésta es pérdida sobre eso.

const MOTIVOS = [
  { v: "caducidad", l: "Caducidad" },
  { v: "mala_preparacion", l: "Mala preparación" },
  { v: "quemado", l: "Quemado" },
  { v: "caida", l: "Se cayó" },
  { v: "devolucion_cliente", l: "Devolución de cliente" },
  { v: "corte_de_energia", l: "Corte de energía" },
  { v: "robo_o_faltante", l: "Robo o faltante" },
  { v: "otro", l: "Otro" },
];
const fmtMXN = n => n != null
  ? `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  : "—";
const hoyISO = () => new Date().toISOString().slice(0, 10);

export default function OpsMermas() {
  const { user } = useAuth();
  const isAdmin = ["corporate_admin", "direccion"].includes(user?.role);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id || "");
  const [catalogo, setCatalogo] = useState([]);
  const [mermas, setMermas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [desde, setDesde] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().slice(0, 10);
  });
  const [hasta, setHasta] = useState(hoyISO);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({ insumo: "", cantidad: "", unidad: "", motivo: "caducidad", nota: "" });
  // Fix WH45 (2026-09-11): cuanto pesa una pieza, cuando el insumo todavia no lo tiene cargado.
  const [pesoPieza, setPesoPieza] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/branches")
      .then(r => {
        const bs = r.data.filter(b => !b.is_corporate);
        setBranches(bs);
        // Fix WH45 (2026-09-11): era `prev || bs[0].id`, y eso dejaba pasar
        // cualquier valor previo aunque no fuera una opcion del selector. Los
        // usuarios corporativos que tienen "Corporativo" como sucursal propia
        // -medido: Gabriela Bautista y Cristian Lugo- arrancaban con ese id, que
        // no esta en la lista porque se filtra por !is_corporate. Un <select> con
        // un value que no matchea ninguna opcion queda en selectedIndex -1: se
        // dibuja EN BLANCO. Y por debajo la pantalla pedia los datos de
        // Corporativo, que no tiene recetario: cero insumos, sin explicacion.
        setBranchId(prev => (prev && bs.some(b => b.id === prev)) ? prev : (bs[0] ? bs[0].id : ""));
      })
      .catch(() => {});
  }, [isAdmin]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const q = branchId ? `&branch_id=${branchId}` : "";
      const [lRes, rRes] = await Promise.all([
        api.get(`/ops/mermas?desde=${desde}&hasta=${hasta}${q}`),
        api.get(`/ops/mermas/resumen?desde=${desde}&hasta=${hasta}${q}`),
      ]);
      setMermas(lRes.data || []);
      setResumen(rRes.data || null);
    } catch { toast.error("No se pudo cargar la merma"); }
    finally { setLoading(false); }
    try {
      const c = await api.get(`/ops/mermas/insumos-disponibles${branchId ? `?branch_id=${branchId}` : ""}`);
      setCatalogo(c.data || []);
    } catch { setCatalogo([]); }
  }, [branchId, desde, hasta]);

  useEffect(() => { if (branchId || !isAdmin) cargar(); }, [cargar, branchId, isAdmin]);

  const guardar = async e => {
    e.preventDefault();
    if (!form.insumo.trim()) { toast.error("Elegí el insumo"); return; }
    const cant = parseFloat(form.cantidad);
    if (!(cant > 0)) { toast.error("La cantidad tiene que ser mayor a cero"); return; }
    setGuardando(true);
    try {
      // Fix WH45 (2026-09-11): si carga en piezas y todavia no se sabe cuanto pesa
      // una, se guarda primero la equivalencia. El sistema no la adivina: una
      // pizza chica y una grande no llevan la misma masa.
      if (esPieza && !elegido?.factor_pieza) {
        const f = parseFloat(pesoPieza);
        if (!(f > 0)) { toast.error(`Deci cuanto pesa una pieza de ${form.insumo}`); setGuardando(false); return; }
        await api.post("/ops/mermas/equivalencias", {
          branch_id: branchId || undefined, insumo: form.insumo.trim(), factor: f,
        });
      }
      await api.post("/ops/mermas", {
        branch_id: branchId || undefined,
        insumo: form.insumo.trim(), cantidad: cant,
        unidad: form.unidad || undefined,
        motivo: form.motivo, nota: form.nota.trim() || undefined,
      });
      toast.success("Merma registrada");
      setForm({ insumo: "", cantidad: "", unidad: "", motivo: "caducidad", nota: "" });
      setPesoPieza("");
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo registrar");
    } finally { setGuardando(false); }
  };

  const borrar = async m => {
    if (!window.confirm(`Borrar la merma de ${m.insumo}?`)) return;
    try { await api.delete(`/ops/mermas/${m.id}`); toast.success("Borrada"); cargar(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo borrar"); }
  };

  const elegido = catalogo.find(c => c.nombre === form.insumo);
  // Fix WH45 (2026-09-11): que se pueda cargar en la unidad en que la gente
  // cuenta. De gramos a kilos hay un factor universal; de piezas a kilos no, y
  // por eso se pregunta una vez por insumo y sucursal.
  const unidadBase = (elegido?.unidad || "").toUpperCase();
  const UNIDADES_POR_BASE = { KG: ["KG", "G", "PZ"], LT: ["LT", "ML", "PZ"], L: ["L", "ML", "PZ"] };
  const unidades = UNIDADES_POR_BASE[unidadBase] || (unidadBase ? [unidadBase] : []);
  const unidadUsada = (form.unidad || unidadBase).toUpperCase();
  const esPieza = ["PZ", "PZA", "PIEZA", "PIEZAS"].includes(unidadUsada) && unidadBase !== "PZ";
  const factorA = { G: 0.001, GR: 0.001, ML: 0.001 }[unidadUsada]
    ?? (esPieza ? (elegido?.factor_pieza || parseFloat(pesoPieza) || 0) : 1);
  const cantBase = (parseFloat(form.cantidad) || 0) * (factorA || 0);

  return (
    <div className="p-6 space-y-5" data-testid="ops-mermas-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Merma</h1>
          <p className="text-sm text-slate-500">
            Lo que se tira, registrado en el momento y valorizado con el costo del insumo.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <select value={branchId} onChange={e => setBranchId(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
              data-testid="mermas-branch-select">
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="mermas-desde" />
          <input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="mermas-hasta" />
        </div>
      </div>

      {resumen && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs text-slate-400">Merma del período</div>
            <div className="text-2xl font-bold text-slate-900">{fmtMXN(resumen.costo_total)}</div>
            <div className="text-xs text-slate-400">{resumen.registros} registros</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs text-slate-400">Motivo principal</div>
            <div className="text-lg font-semibold text-slate-900">
              {resumen.por_motivo?.[0]
                ? (MOTIVOS.find(m => m.v === resumen.por_motivo[0].motivo)?.l || resumen.por_motivo[0].motivo)
                : "—"}
            </div>
            <div className="text-xs text-slate-400">
              {resumen.por_motivo?.[0] ? fmtMXN(resumen.por_motivo[0].costo_total) : ""}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs text-slate-400">Insumo que más se tira</div>
            <div className="text-lg font-semibold text-slate-900 truncate">
              {resumen.por_insumo?.[0]?.insumo || "—"}
            </div>
            <div className="text-xs text-slate-400">
              {resumen.por_insumo?.[0] ? fmtMXN(resumen.por_insumo[0].costo_total) : ""}
            </div>
          </div>
        </div>
      )}

      {resumen && resumen.registros_sin_valorizar > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800"
             data-testid="mermas-sin-precio">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {/* Fix WH45 (2026-09-11): antes este cartel decia, de TODOS los
                registros sin precio, que el insumo no estaba en el catalogo. Para
                MASA PIZZA en Puerto Vallarta eso era falso: si esta, lo que no
                tiene es precio. Son dos problemas distintos y se arreglan
                distinto, asi que ahora se nombran distinto. */}
            <b>{resumen.registros_sin_valorizar}</b> registros no suman al total, así que
            {" "}<b>el total en pesos está corto</b>.
            {resumen.insumos_sin_precio?.length > 0 && <>
              {" "}Sin precio todavía (son elaborados y su receta no permite calcularlo):
              {" "}{resumen.insumos_sin_precio.join(", ")}.
            </>}
            {resumen.insumos_sin_catalogo?.length > 0 && <>
              {" "}No están en el catálogo de esta sucursal (puede ser que allá se llamen distinto):
              {" "}{resumen.insumos_sin_catalogo.join(", ")}.
            </>}
          </span>
        </div>
      )}

      <form onSubmit={guardar} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3"
            data-testid="mermas-form">
        <div className="flex items-center gap-2">
          <Plus className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Registrar merma</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Insumo</label>
            <input list="lista-insumos" value={form.insumo}
              onChange={e => setForm(f => ({ ...f, insumo: e.target.value }))}
              placeholder={catalogo.length ? "Escribí o elegí" : "Escribí el insumo"}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="mermas-insumo" />
            <datalist id="lista-insumos">
              {catalogo.map(c => <option key={c.nombre} value={c.nombre} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Cantidad</label>
            <div className="flex gap-1">
              <input type="number" step="0.001" min="0" value={form.cantidad}
                onChange={e => setForm(f => ({ ...f, cantidad: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                data-testid="mermas-cantidad" />
              {unidades.length > 1 && (
                <select value={unidadUsada} onChange={e => setForm(f => ({ ...f, unidad: e.target.value }))}
                  className="border border-slate-200 rounded-lg px-2 py-2 text-sm shrink-0"
                  data-testid="mermas-unidad">
                  {unidades.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Motivo</label>
            <select value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="mermas-motivo">
              {MOTIVOS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <label className="block text-xs text-slate-500 mb-1">Nota (opcional)</label>
            <input value={form.nota} onChange={e => setForm(f => ({ ...f, nota: e.target.value }))}
              placeholder="Qué pasó" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="mermas-nota" />
          </div>
          <button type="submit" disabled={guardando}
            className="flex items-center justify-center gap-1.5 px-5 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold text-slate-900 hover:bg-yellow-300 disabled:opacity-60"
            data-testid="mermas-guardar">
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Registrar
          </button>
        </div>
        {esPieza && !elegido?.factor_pieza && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200"
               data-testid="mermas-peso-pieza">
            <span className="text-xs text-slate-600 shrink-0">
              ¿Cuánto {unidadBase.toLowerCase() || "pesa"} una pieza de {form.insumo || "este insumo"}?
            </span>
            <input type="number" step="0.001" min="0" value={pesoPieza}
              onChange={e => setPesoPieza(e.target.value)} placeholder="0.46"
              className="w-24 border border-slate-200 rounded-lg px-2 py-1 text-sm" />
            <span className="text-[11px] text-slate-400">Se pregunta una sola vez y queda guardado.</span>
          </div>
        )}
        {elegido && (
          <div className="text-[11px] text-slate-400">
            {elegido.departamento} · {elegido.costo_unitario > 0
              ? <>cuesta {fmtMXN(elegido.costo_unitario)} por {elegido.unidad || "unidad"}
                  {elegido.costo_calculado && <span className="text-slate-300"> (calculado desde la receta)</span>}</>
              : <span className="text-amber-600">todavía no tiene precio: es un elaborado sin receta cargada</span>}
            {cantBase > 0 && elegido.costo_unitario > 0 && <>
              {" · "}esta merma vale <b>{fmtMXN(elegido.costo_unitario * cantBase)}</b>
              {unidadUsada !== unidadBase && <> ({cantBase.toFixed(3)} {unidadBase})</>}
            </>}
          </div>
        )}
      </form>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : mermas.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            No hay merma registrada en este período.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {mermas.map(m => (
              <div key={m.id} className="flex items-center justify-between gap-3 p-3" data-testid="merma-row">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {m.insumo}{" "}
                    {/* Fix WH45 (2026-09-11): quien cargo tiene que reconocer lo que
                        cargo. Si conto 3 piezas, ve "3 PZ" y entre parentesis los
                        kilos con que se valorizo. */}
                    <span className="text-slate-400 font-normal">
                      · {m.cantidad_ingresada ?? m.cantidad} {m.unidad_ingresada || m.unidad}
                      {m.unidad_ingresada && m.unidad_ingresada !== m.unidad
                        ? ` (${m.cantidad} ${m.unidad})` : ""}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-400">
                    {m.fecha} · {MOTIVOS.find(x => x.v === m.motivo)?.l || m.motivo}
                    {isAdmin && m.branch_name ? ` · ${m.branch_name}` : ""}
                    {m.registrada_por_nombre ? ` · ${m.registrada_por_nombre}` : ""}
                    {m.nota ? ` · ${m.nota}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-sm font-semibold ${m.valorizada ? "text-slate-900" : "text-slate-300"}`}
                        title={m.valorizada ? "" : "Sin precio: el insumo no está en el catálogo"}>
                    {m.valorizada ? fmtMXN(m.costo_total) : "sin precio"}
                  </span>
                  <button onClick={() => borrar(m)} className="text-slate-300 hover:text-red-500 transition-colors"
                          title="Borrar (sólo el mismo día)" data-testid="merma-borrar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
